import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { useMasters } from "../form/engine";
import { t, useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const OWNER_ROLES = ["cloud_kitchen", "cafe_owner", "vendor", "admin"];
const KINDS = ["cloud_kitchen", "cafe", "restaurant", "shared"];
const SLOTS = ["Morning", "Evening", "Full day"];

// KitchenHub — rentable kitchen spaces: owners list, chefs book by the hour.
export const KitchenHubPanel = () => {
  const { user } = useAuth();
  const lang = useLang();
  const masters = useMasters();
  const isOwner = OWNER_ROLES.includes(user.role);
  const [tab, setTab] = useState("browse");
  const [spaces, setSpaces] = useState(null);
  const [mine, setMine] = useState([]);
  const [requests, setRequests] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [q, setQ] = useState("");
  const [bookingFor, setBookingFor] = useState(null);
  const [bForm, setBForm] = useState({ date: "", slot: "Morning", hours: 2, purpose: "" });
  const [sForm, setSForm] = useState({ name: "", kind: "cloud_kitchen", location: "", amenities: [], hourly_rate: "", capacity: 4 });
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/kitchens/spaces?q=${encodeURIComponent(q)}`, headers()).then((r) => setSpaces(r.data.spaces)).catch(() => setSpaces([]));
    if (isOwner) {
      axios.get(`${API}/kitchens/spaces/mine`, headers()).then((r) => setMine(r.data.spaces)).catch(() => {});
      axios.get(`${API}/kitchens/bookings/incoming`, headers()).then((r) => setRequests(r.data.bookings)).catch(() => {});
    }
    axios.get(`${API}/kitchens/bookings/mine`, headers()).then((r) => setMyBookings(r.data.bookings)).catch(() => {});
  };
  useEffect(load, [q]);

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/kitchens/bookings`, { space_id: bookingFor.id, ...bForm, hours: Number(bForm.hours) }, headers());
      toast.success(`Booked! ₹${data.total} · ${bForm.date} ${bForm.slot}`);
      setBookingFor(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createSpace = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/kitchens/spaces`, { ...sForm, hourly_rate: Number(sForm.hourly_rate), capacity: Number(sForm.capacity) }, headers());
      toast.success("Kitchen space listed 🏭");
      setSForm({ name: "", kind: "cloud_kitchen", location: "", amenities: [], hourly_rate: "", capacity: 4 });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/kitchens/bookings/${id}/status`, { status }, headers());
      toast.success(`Booking ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const tabs = [
    { id: "browse", label: `🏭 ${t(lang, "kh_spaces")}` },
    ...(isOwner ? [{ id: "mine", label: `📋 ${t(lang, "kh_mine")}` }, { id: "requests", label: `📥 ${t(lang, "kh_incoming")}` }] : []),
    { id: "bookings", label: `🗓️ ${t(lang, "kh_bookings")}` },
  ];

  const inputCls = "w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60";

  return (
    <div className="space-y-5" data-testid="panel-kitchenhub">
      <div className="flex flex-wrap gap-2">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`kh-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`rounded-full px-4 py-2 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === tb.id ? "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white" : "border border-white/10 text-slate-400 hover:text-white"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "browse" && (
        <>
          <input data-testid="kh-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Search by name or area…" className={inputCls} />
          {spaces === null ? (
            <LoaderCircle className="h-5 w-5 animate-spin text-[#06B6D4]" />
          ) : spaces.length === 0 ? (
            <p className="text-sm text-slate-500">No kitchen spaces yet{isOwner ? " — list yours in My listings" : ""}.</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {spaces.map((s) => (
                <div key={s.id} data-testid={`kh-space-${s.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold">{s.name}</p>
                      <p className="font-jbmono text-[10px] text-slate-500">{s.kind.replace("_", " ")} · {s.location}</p>
                    </div>
                    <span className="shrink-0 font-jbmono text-xs font-bold text-[#22C55E]">₹{s.hourly_rate}{t(lang, "kh_rate")}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.amenities.map((a) => (
                      <span key={a} className="rounded-full bg-white/5 px-2 py-0.5 font-jbmono text-[9px] text-slate-400">{a}</span>
                    ))}
                    <span className="rounded-full bg-white/5 px-2 py-0.5 font-jbmono text-[9px] text-slate-400">👥 {s.capacity}</span>
                  </div>
                  {bookingFor?.id === s.id ? (
                    <div className="mt-3 space-y-2 rounded-xl border border-[#06B6D4]/30 bg-[#06B6D4]/[0.05] p-3">
                      <input data-testid="kh-book-date" type="date" value={bForm.date} onChange={(e) => setBForm({ ...bForm, date: e.target.value })} className={inputCls} />
                      <div className="flex gap-1.5">
                        {SLOTS.map((sl) => (
                          <button key={sl} data-testid={`kh-slot-${sl.toLowerCase().replace(" ", "-")}`} onClick={() => setBForm({ ...bForm, slot: sl })}
                            className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${bForm.slot === sl ? "bg-[#06B6D4] text-[#09090B]" : "border border-white/10 text-slate-400"}`}>
                            {sl}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input data-testid="kh-book-hours" type="number" min="1" max="24" value={bForm.hours} onChange={(e) => setBForm({ ...bForm, hours: e.target.value })} placeholder="Hours" className={inputCls} />
                        <input data-testid="kh-book-purpose" value={bForm.purpose} onChange={(e) => setBForm({ ...bForm, purpose: e.target.value })} placeholder="Purpose (optional)" className={inputCls} />
                      </div>
                      <div className="flex gap-2">
                        <button data-testid="kh-book-confirm" onClick={book} disabled={busy || !bForm.date || !bForm.hours}
                          className="flex-1 rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] py-2.5 font-display text-xs font-bold uppercase text-[#09090B] disabled:opacity-50">
                          {busy ? <LoaderCircle className="mx-auto h-3.5 w-3.5 animate-spin" /> : `${t(lang, "kh_book")} · ₹${s.hourly_rate * (Number(bForm.hours) || 0)}`}
                        </button>
                        <button onClick={() => setBookingFor(null)} className="rounded-full border border-white/15 px-4 text-xs text-slate-400">{t(lang, "cancel")}</button>
                      </div>
                    </div>
                  ) : (
                    <button data-testid={`kh-book-${s.id.slice(-6)}`} onClick={() => setBookingFor(s)}
                      className="mt-3 w-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white">
                      {t(lang, "kh_book")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "mine" && isOwner && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="kh-create">
            <p className="font-display text-sm font-bold">{t(lang, "kh_add")}</p>
            <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <input data-testid="kh-new-name" value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} placeholder="Space name" className={inputCls} />
              <input data-testid="kh-new-location" value={sForm.location} onChange={(e) => setSForm({ ...sForm, location: e.target.value })} placeholder="Location (area, city)" className={inputCls} />
              <input data-testid="kh-new-rate" type="number" value={sForm.hourly_rate} onChange={(e) => setSForm({ ...sForm, hourly_rate: e.target.value })} placeholder="Hourly rate ₹" className={inputCls} />
              <input data-testid="kh-new-capacity" type="number" value={sForm.capacity} onChange={(e) => setSForm({ ...sForm, capacity: e.target.value })} placeholder="Capacity (people)" className={inputCls} />
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {KINDS.map((k) => (
                <button key={k} data-testid={`kh-kind-${k}`} onClick={() => setSForm({ ...sForm, kind: k })}
                  className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${sForm.kind === k ? "bg-[#7C3AED] text-white" : "border border-white/10 text-slate-400"}`}>
                  {k.replace("_", " ")}
                </button>
              ))}
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {(masters.kitchen_amenities || []).map((a) => (
                <button key={a} data-testid={`kh-amenity-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setSForm({ ...sForm, amenities: sForm.amenities.includes(a) ? sForm.amenities.filter((x) => x !== a) : [...sForm.amenities, a] })}
                  className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${sForm.amenities.includes(a) ? "bg-[#06B6D4] text-[#09090B]" : "border border-white/10 text-slate-400"}`}>
                  {a}
                </button>
              ))}
            </div>
            <button data-testid="kh-create-submit" onClick={createSpace} disabled={busy || !sForm.name || !sForm.location || !sForm.hourly_rate}
              className="mt-3 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : t(lang, "kh_add")}
            </button>
          </div>
          {mine.map((s) => (
            <div key={s.id} data-testid={`kh-my-${s.id.slice(-6)}`} className="flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">{s.name} <span className={s.active ? "text-[#22C55E]" : "text-slate-500"}>· {s.active ? "live" : "paused"}</span></p>
                <p className="font-jbmono text-[10px] text-slate-500">₹{s.hourly_rate}/hr · {s.location}</p>
              </div>
              <button data-testid={`kh-toggle-${s.id.slice(-6)}`} onClick={async () => { await axios.post(`${API}/kitchens/spaces/${s.id}/toggle`, {}, headers()); load(); }}
                className="rounded-full border border-white/15 px-4 py-2 text-xs text-slate-300 hover:text-white">
                {s.active ? "Pause" : "Activate"}
              </button>
            </div>
          ))}
        </div>
      )}

      {(tab === "requests" || tab === "bookings") && (
        <div className="space-y-2.5">
          {(tab === "requests" ? requests : myBookings).length === 0 && <p className="text-sm text-slate-500">Nothing here yet.</p>}
          {(tab === "requests" ? requests : myBookings).map((b) => (
            <div key={b.id} data-testid={`kh-booking-${b.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold">{tab === "requests" ? b.renter_name : b.space_name}</p>
                  <p className="font-jbmono text-[10px] text-slate-500">{b.date} · {b.slot} · {b.hours}h{b.purpose ? ` · ${b.purpose}` : ""}</p>
                </div>
                <span className="shrink-0 font-jbmono text-xs font-bold text-[#22C55E]">₹{b.total}</span>
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                  b.status === "confirmed" ? "bg-[#22C55E]/10 text-[#22C55E]" : b.status === "requested" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-white/5 text-slate-400"}`}>
                  {b.status}
                </span>
                {tab === "requests" && b.status === "requested" && (
                  <button data-testid={`kh-confirm-${b.id.slice(-6)}`} onClick={() => setStatus(b.id, "confirmed")}
                    className="rounded-full bg-[#22C55E] px-4 py-1.5 text-[10px] font-bold text-[#09090B]">Confirm</button>
                )}
                {tab === "requests" && b.status === "confirmed" && (
                  <button data-testid={`kh-complete-${b.id.slice(-6)}`} onClick={() => setStatus(b.id, "completed")}
                    className="rounded-full bg-[#06B6D4] px-4 py-1.5 text-[10px] font-bold text-[#09090B]">Complete</button>
                )}
                {["requested"].includes(b.status) && (
                  <button data-testid={`kh-cancel-${b.id.slice(-6)}`} onClick={() => setStatus(b.id, "cancelled")}
                    className="rounded-full border border-white/15 px-4 py-1.5 text-[10px] text-slate-400 hover:text-white">{t(lang, "cancel")}</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
